import { useTranslation } from 'react-i18next';
import { Button } from '@/common/components/Button/Button';
import { PageHeader } from '@/common/components/PageHeader/PageHeader';
import { Stack } from '@/common/components/Stack/Stack';
import { Translate } from '@/common/components/Translate/Translate';
import { useAuth } from '@/common/contexts/authentication/AuthProvider';
import { useAuthenticationContext } from '@/common/contexts/authentication/AuthenticationContext';
import { useNotificationsContext } from '@/common/contexts/notifications/NotificationsContext';
import { NotificationsService } from '@/common/integrations/notifications/notifications-service';
import { AuthFormLayout } from '@/common/layouts/AuthLayout/AuthLayout';
import { notify } from '@/common/utils/toast/toast';
import { usePageViewTracking } from '@/web/hooks/usePageViewTracking';

export function NotificationsPage() {
  usePageViewTracking('notifications_page');
  const { t } = useTranslation();
  const { signIn } = useAuth();
  const { setNotificationToken } = useNotificationsContext();
  const authenticationContext = useAuthenticationContext();

  const onSignIn = async () => {
    const { authState } = authenticationContext;
    if (authState && 'user' in authState) {
      await signIn(authState);
    }
  };

  const notifyError = () => {
    notify({
      type: 'error',
      message: (
        <Translate
          i18nKey={
            'authentication.notifications.errors.notifications_activation_failed'
          }
        />
      ),
    });
  };

  const onActivateNotifications = async () => {
    try {
      const token =
        await NotificationsService.registerForPushNotificationsAsync();
      if (token) {
        setNotificationToken(token);
        await onSignIn();
        return;
      }
      notifyError();
    } catch (err: any) {
      notifyError();
    }
  };

  return (
    <AuthFormLayout
      footer={
        <Stack gap="md">
          <Button type="submit" onPress={onActivateNotifications}>
            {t('authentication.notifications.activate')}
          </Button>
          <Button type="button" variant="tertiary" onPress={onSignIn}>
            {t('authentication.notifications.dismiss')}
          </Button>
        </Stack>
      }
    >
      <Stack gap="xl">
        <PageHeader
          title={t('authentication.notifications.title')}
          description={t('authentication.notifications.description')}
        />
      </Stack>
    </AuthFormLayout>
  );
}
